import React, { useEffect, useRef, useState } from 'react';
import './ApiDocs.css';

/* ------------------------------------------------------------------ */
/*  Endpoint data                                                      */
/* ------------------------------------------------------------------ */

const RESOURCES = [
  {
    id: 'submissions',
    title: 'Submissions',
    endpoints: [
      {
        method: 'POST',
        path: '/submissions',
        groups: ['underwriter', 'leadership', 'broker'],
        desc: 'Create a new submission.',
        body: `{
  "productId": "string",
  "brokerId": "string",
  "insuredName": "string",
  "source": "email | upload | api",
  "structuredData": { }
}`,
        response: `{
  "submissionId": "string",
  "accountId": "string",
  "status": "new"
}`,
      },
      {
        method: 'GET',
        path: '/submissions',
        groups: ['underwriter', 'leadership', 'broker'],
        desc: 'List submissions. Brokers see only their own submissions.',
        note: 'Query params: ?status=&assignedUW=',
      },
      {
        method: 'GET',
        path: '/submissions/{id}',
        groups: ['underwriter', 'leadership', 'broker'],
        desc: 'Get submission detail by ID.',
      },
      {
        method: 'PUT',
        path: '/submissions/{id}',
        groups: ['underwriter', 'leadership'],
        desc: 'Update a submission.',
        body: `{
  "status": "string",
  "productId": "string"
}`,
      },
      {
        method: 'POST',
        path: '/submissions/{id}/assign',
        groups: ['underwriter', 'leadership'],
        desc: 'Assign an underwriter to a submission. Checks workload capacity before assignment.',
        body: `{
  "underwriterId": "string"
}`,
      },
      {
        method: 'GET',
        path: '/submissions/{id}/missing-data',
        groups: ['underwriter', 'leadership'],
        desc: 'Get missing data flags for a submission.',
      },
    ],
  },
  {
    id: 'underwriting',
    title: 'Underwriting',
    endpoints: [
      {
        method: 'GET',
        path: '/underwriting/{submissionId}/canvas',
        groups: ['underwriter', 'leadership'],
        desc: 'Full underwriting canvas for a submission.',
        response: `{
  "submission": { },
  "account": { },
  "assessment": { },
  "documents": [ ],
  "productGuidelines": { }
}`,
      },
      {
        method: 'POST',
        path: '/underwriting/{submissionId}/assessment',
        groups: ['underwriter', 'leadership'],
        desc: 'Create a risk assessment for a submission.',
        body: `{
  "riskScore": 0-10,
  "riskDrivers": ["string"],
  "disqualifiers": ["string"],
  "confidenceLevel": 0-1,
  "notes": "string"
}`,
      },
      {
        method: 'PUT',
        path: '/underwriting/{submissionId}/assessment',
        groups: ['underwriter', 'leadership'],
        desc: 'Update an existing risk assessment.',
      },
      {
        method: 'POST',
        path: '/underwriting/{submissionId}/decision',
        groups: ['underwriter', 'leadership'],
        desc: 'Record an underwriting decision.',
        body: `{
  "decision": "bind" | "decline" | "refer",
  "rationale": "string"
}`,
      },
      {
        method: 'GET',
        path: '/underwriting/{submissionId}/similar-risks',
        groups: ['underwriter', 'leadership'],
        desc: 'Similar past risks surfaced for higher-risk submissions (0-5 tier).',
      },
    ],
  },
  {
    id: 'accounts',
    title: 'Accounts',
    endpoints: [
      {
        method: 'POST',
        path: '/accounts',
        groups: ['underwriter', 'leadership'],
        desc: 'Create a new account.',
      },
      {
        method: 'GET',
        path: '/accounts',
        groups: ['underwriter', 'leadership', 'broker'],
        desc: 'List accounts.',
      },
      {
        method: 'GET',
        path: '/accounts/{id}',
        groups: ['underwriter', 'leadership', 'broker'],
        desc: 'Account detail with full history.',
      },
      {
        method: 'GET',
        path: '/accounts/{id}/movement',
        groups: ['underwriter', 'leadership'],
        desc: 'BOR/AOR history and broker transitions for an account.',
      },
    ],
  },
  {
    id: 'brokers',
    title: 'Brokers',
    endpoints: [
      {
        method: 'GET',
        path: '/brokers',
        groups: ['underwriter', 'leadership'],
        desc: 'List all brokers.',
      },
      {
        method: 'POST',
        path: '/brokers',
        groups: ['underwriter', 'leadership'],
        desc: 'Create a new broker.',
      },
      {
        method: 'GET',
        path: '/brokers/{id}',
        groups: ['underwriter', 'leadership'],
        desc: 'Get broker detail by ID.',
      },
      {
        method: 'PUT',
        path: '/brokers/{id}',
        groups: ['underwriter', 'leadership'],
        desc: 'Update a broker.',
      },
      {
        method: 'DELETE',
        path: '/brokers/{id}',
        groups: ['underwriter', 'leadership'],
        desc: 'Delete a broker.',
      },
    ],
  },
  {
    id: 'products',
    title: 'Products',
    endpoints: [
      {
        method: 'GET',
        path: '/products',
        groups: ['underwriter', 'leadership'],
        desc: 'List all products.',
      },
      {
        method: 'POST',
        path: '/products',
        groups: ['leadership'],
        desc: 'Create a new product.',
        body: `{
  "name": "string",
  "description": "string",
  "appetiteDefinition": { },
  "pricingGuardrails": { },
  "underwritingGuidelines": { },
  "exceptionRules": { }
}`,
      },
      {
        method: 'GET',
        path: '/products/{id}',
        groups: ['underwriter', 'leadership'],
        desc: 'Get product detail by ID.',
      },
      {
        method: 'PUT',
        path: '/products/{id}',
        groups: ['leadership'],
        desc: 'Update a product.',
      },
      {
        method: 'DELETE',
        path: '/products/{id}',
        groups: ['leadership'],
        desc: 'Delete a product.',
      },
    ],
  },
  {
    id: 'quotes',
    title: 'Quotes',
    endpoints: [
      {
        method: 'POST',
        path: '/quotes',
        groups: ['underwriter', 'leadership'],
        desc: 'Generate a new quote.',
        body: `{
  "submissionId": "string",
  "accountId": "string",
  "productId": "string",
  "premium": 0,
  "deductible": 0,
  "limit": 0,
  "terms": { },
  "rationale": "string"
}`,
      },
      {
        method: 'GET',
        path: '/quotes/{id}',
        groups: ['underwriter', 'leadership', 'broker'],
        desc: 'Quote detail with coverage comparison.',
      },
      {
        method: 'POST',
        path: '/quotes/{id}/deliver',
        groups: ['underwriter', 'leadership'],
        desc: 'Deliver a quote to the broker.',
      },
    ],
  },
  {
    id: 'leadership',
    title: 'Leadership',
    endpoints: [
      {
        method: 'GET',
        path: '/leadership/dashboard',
        groups: ['leadership'],
        desc: 'Portfolio overview with status and tier breakdowns, capacity utilization.',
      },
      {
        method: 'GET',
        path: '/leadership/overrides',
        groups: ['leadership'],
        desc: 'Override patterns across underwriters.',
      },
      {
        method: 'GET',
        path: '/leadership/accumulation',
        groups: ['leadership'],
        desc: 'Risk accumulation across products.',
      },
      {
        method: 'GET',
        path: '/leadership/capacity/{providerId}',
        groups: ['leadership'],
        desc: 'Capacity provider dashboard for a specific provider.',
      },
    ],
  },
  {
    id: 'communications',
    title: 'Communications',
    endpoints: [
      {
        method: 'POST',
        path: '/communications',
        groups: ['underwriter', 'leadership'],
        desc: 'Send a communication.',
        body: `{
  "accountId": "string",
  "submissionId": "string",
  "toEmails": ["string"],
  "subject": "string",
  "body": "string",
  "commType": "quote_delivery | info_request | decline_notice"
}`,
      },
      {
        method: 'GET',
        path: '/communications/{accountId}',
        groups: ['underwriter', 'leadership'],
        desc: 'Communication history for an account.',
      },
    ],
  },
  {
    id: 'documents',
    title: 'Documents',
    endpoints: [
      {
        method: 'POST',
        path: '/documents/upload',
        groups: ['underwriter', 'leadership', 'broker'],
        desc: 'Upload a document. Returns a presigned URL for direct upload.',
      },
      {
        method: 'GET',
        path: '/documents/{id}',
        groups: ['underwriter', 'leadership', 'broker'],
        desc: 'Document metadata and download URL.',
      },
      {
        method: 'GET',
        path: '/documents/submission/{submissionId}',
        groups: ['underwriter', 'leadership'],
        desc: 'All documents for a submission.',
      },
    ],
  },
  {
    id: 'users',
    title: 'Users',
    endpoints: [
      {
        method: 'POST',
        path: '/users',
        groups: ['leadership'],
        desc: 'Create a new user.',
      },
      {
        method: 'GET',
        path: '/users',
        groups: ['leadership'],
        desc: 'List all users.',
      },
      {
        method: 'GET',
        path: '/users/{id}',
        groups: ['underwriter', 'leadership'],
        desc: 'Get user detail by ID.',
      },
      {
        method: 'PUT',
        path: '/users/{id}',
        groups: ['leadership'],
        desc: 'Update a user.',
      },
      {
        method: 'GET',
        path: '/users/{id}/workload',
        groups: ['underwriter', 'leadership'],
        desc: 'Workload detail with utilization and assigned submissions breakdown.',
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

function Endpoint({ ep }) {
  return (
    <div className="api-docs__endpoint">
      <div className="api-docs__endpoint-header">
        <span className={`api-docs__method api-docs__method--${ep.method.toLowerCase()}`}>
          {ep.method}
        </span>
        <span className="api-docs__path">{ep.path}</span>
        <div className="api-docs__endpoint-groups">
          {ep.groups.map((g) => (
            <span key={g} className="api-docs__endpoint-group">{g}</span>
          ))}
        </div>
      </div>

      <p className="api-docs__endpoint-desc">{ep.desc}</p>

      {ep.note && <p className="api-docs__endpoint-note">{ep.note}</p>}

      {ep.body && (
        <div className="api-docs__code-block">
          <p className="api-docs__code-label">Request Body</p>
          <pre className="api-docs__code">{ep.body}</pre>
        </div>
      )}

      {ep.response && (
        <div className="api-docs__code-block">
          <p className="api-docs__code-label">Response</p>
          <pre className="api-docs__code">{ep.response}</pre>
        </div>
      )}
    </div>
  );
}

export function ApiDocs() {
  const [activeId, setActiveId] = useState(RESOURCES[0].id);
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = [];

    RESOURCES.forEach(({ id }) => {
      const el = sectionRefs.current[id];
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveId(id);
          }
        },
        { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const refSetter = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  return (
    <div className="api-docs">
      {/* Sticky sidebar TOC */}
      <nav className="api-docs__toc" aria-label="API sections">
        <div className="api-docs__toc-inner">
          <p className="api-docs__toc-title">Resources</p>
          <ul className="api-docs__toc-list">
            {RESOURCES.map((r) => (
              <li key={r.id}>
                <a
                  href={`#${r.id}`}
                  className={`api-docs__toc-link${activeId === r.id ? ' api-docs__toc-link--active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    sectionRefs.current[r.id]?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  {r.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Main content */}
      <div className="api-docs__content">
        {/* Header */}
        <div className="api-docs__header card">
          <h1 className="api-docs__header-title">API Reference</h1>

          <div className="api-docs__meta">
            <div className="api-docs__meta-row">
              <span className="api-docs__meta-label">Base URL</span>
              <code className="api-docs__meta-code">https://api.engine.dev.dextris.nevado.ai</code>
            </div>
            <div className="api-docs__meta-row">
              <span className="api-docs__meta-label">Authentication</span>
              <span className="api-docs__meta-value">Bearer token via AWS Cognito JWT (ID Token)</span>
            </div>
            <div className="api-docs__meta-row">
              <span className="api-docs__meta-label">Header</span>
              <code className="api-docs__meta-code">Authorization: Bearer &lt;id_token&gt;</code>
            </div>
          </div>

          <div className="api-docs__response-format">
            <p className="api-docs__response-format-title">Standard Response Format</p>
            <pre className="api-docs__code">{`{
  "success": boolean,
  "message": "string",
  "data": any,
  "timestamp": "ISO 8601"
}`}</pre>
          </div>

          <div style={{ marginTop: 'var(--space-md)' }}>
            <p className="api-docs__response-format-title">User Groups</p>
            <div className="api-docs__groups">
              <span className="api-docs__group-badge">underwriter</span>
              <span className="api-docs__group-badge">leadership</span>
              <span className="api-docs__group-badge">broker</span>
              <span className="api-docs__group-badge">capacity-provider</span>
            </div>
          </div>
        </div>

        {/* Resource sections */}
        {RESOURCES.map((resource) => (
          <section
            key={resource.id}
            id={resource.id}
            className="api-docs__resource"
            ref={refSetter(resource.id)}
          >
            <div className="api-docs__resource-card">
              <div className="api-docs__resource-header">
                <h2 className="api-docs__resource-title">
                  {resource.title}
                  <span className="api-docs__resource-count">
                    {resource.endpoints.length} endpoint{resource.endpoints.length !== 1 ? 's' : ''}
                  </span>
                </h2>
              </div>

              {resource.endpoints.map((ep, i) => (
                <Endpoint key={i} ep={ep} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
